/**
 * @fileoverview Civil & Criminal Offences review workspace (MOR-672)
 * @module components/jobs/phases/review/civilCriminalOffences/CivilCriminalOffencesWorkspace
 *
 * Mirrors the Flagged Transactions workspace: an overview header with the
 * case totals, a master table (one row per offence head), a focused head view,
 * and a master chat panel. Reads the reactive public queries; writes through
 * the lock-aware public mutations.
 */

import { useEffect, useRef, useState } from "react";
import { convexQuery, useConvexMutation } from "@convex-dev/react-query";
import { useQuery, useMutation } from "@tanstack/react-query";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import { toast } from "sonner";
import {
  ArrowLeft,
  Lock,
  LockOpen,
  MessageSquare,
  RefreshCw,
  Scale,
} from "lucide-react";
import { Button } from "@/ui/primitives/button";
import { OffenceMasterTable } from "./OffenceMasterTable";
import { OffenceFocusedHead } from "./OffenceFocusedHead";
import { OffenceAllFindingsTable } from "./OffenceAllFindingsTable";
import { OffenceMasterChatSheet } from "./OffenceMasterChatSheet";
import { OffencesExportButton } from "./OffencesExportButton";
import { OffencesDeferredState } from "./OffencesDeferredState";
import {
  fmtDate,
  agentText,
  INSOLVENCY_BASIS_LABEL,
  type OffenceHeadKey,
  type OffenceWorkspaceUI,
} from "./offenceShared";
import { isPersonalInsolvencyAppointmentType } from "@cvx/lib/appointmentTypes";

const offences = api.jobs.summarization.agents.civilCriminalOffences;

export function CivilCriminalOffencesWorkspace({
  jobId,
  isViewer = false,
  onBack,
  initialHead,
}: {
  jobId: Id<"jobs">;
  isViewer?: boolean;
  onBack: () => void;
  /**
   * `?head=` deep link. An unknown key falls through to the master table,
   * because the focused view only renders once the key resolves against the
   * loaded workspace.
   */
  initialHead?: string;
}) {
  const [focusedHead, setFocusedHead] = useState<OffenceHeadKey | null>(
    (initialHead as OffenceHeadKey | undefined) ?? null,
  );
  const [view, setView] = useState<"heads" | "all">("heads");
  const [chatOpen, setChatOpen] = useState(false);

  // Catches a ?head= change while this workspace is already mounted.
  useEffect(() => {
    if (initialHead) setFocusedHead(initialHead as OffenceHeadKey);
  }, [initialHead]);

  // Opening a head swaps the view in place, so the scroll container keeps its
  // previous offset and lands mid-page below the action buttons. The scrollable
  // element is an ancestor in the job layout (not this component), so walk up
  // from the freshly-rendered head view and reset every scrollable ancestor to
  // the top. rAF lets the new view lay out first.
  const focusedRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!focusedHead) return;
    const reset = () => {
      let node: HTMLElement | null = focusedRef.current;
      while (node) {
        if (node.scrollTop > 0) node.scrollTop = 0;
        node = node.parentElement;
      }
      window.scrollTo({ top: 0 });
    };
    const raf = requestAnimationFrame(reset);
    return () => cancelAnimationFrame(raf);
  }, [focusedHead]);

  const { data } = useQuery(
    convexQuery(offences.publicQueries.getWorkspace, { jobId }),
  );
  const workspace = data as OffenceWorkspaceUI | undefined;

  const { mutateAsync: reRunHeads, isPending: rerunning } = useMutation({
    mutationFn: useConvexMutation(offences.publicMutations.reRunHeads),
  });
  const { mutateAsync: setCaseLock } = useMutation({
    mutationFn: useConvexMutation(offences.publicMutations.setCaseLock),
  });

  // MOR-661: offence heads are deferred on bankruptcy jobs. Any rows a
  // bankruptcy job accumulated before the regime gate existed are retired
  // lazily on mount (idempotent; a no-op on corporate jobs and for viewers).
  const isBankruptcy = isPersonalInsolvencyAppointmentType(
    workspace?.appointmentType,
  );
  const retireOffenceRows = useMutation({
    mutationFn: useConvexMutation(
      offences.publicMutations.retireOffenceRowsForBankruptcy,
    ),
  });
  const retireOffence = retireOffenceRows.mutate;
  useEffect(() => {
    if (isBankruptcy) retireOffence({ jobId });
  }, [isBankruptcy, jobId, retireOffence]);

  if (!workspace) {
    return (
      <div className="flex min-h-full flex-1 items-center justify-center bg-muted">
        <Scale className="h-6 w-6 animate-pulse text-muted-foreground" />
      </div>
    );
  }

  if (isBankruptcy) return <OffencesDeferredState onBack={onBack} />;

  const { heads, caseLocked, insolvency, totals } = workspace;

  if (focusedHead) {
    const head = heads.find((h) => h.headKey === focusedHead);
    if (head) {
      return (
        <div
          ref={focusedRef}
          className="flex min-h-full flex-1 flex-col bg-muted p-6"
        >
          <OffenceFocusedHead
            jobId={jobId}
            head={head}
            isViewer={isViewer}
            insolvency={insolvency}
            onBack={() => setFocusedHead(null)}
          />
          <OffenceMasterChatSheet
            open={chatOpen}
            onOpenChange={setChatOpen}
            jobId={jobId}
            agentBusy={workspace.master.running}
            canSend={!isViewer}
          />
        </div>
      );
    }
  }

  return (
    <div className="flex min-h-full flex-1 flex-col bg-muted p-6">
      <div className="mb-4 flex items-center justify-between">
        <Button
          variant="outline"
          size="sm"
          onClick={onBack}
          className="gap-1.5"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Investigations
        </Button>
        <div className="flex items-center gap-2">
          <div className="flex items-center rounded-md border bg-background p-0.5">
            <button
              onClick={() => setView("heads")}
              className={`rounded px-2.5 py-1 text-xs font-medium ${view === "heads" ? "bg-muted" : "text-muted-foreground"}`}
            >
              Heads
            </button>
            <button
              onClick={() => setView("all")}
              className={`rounded px-2.5 py-1 text-xs font-medium ${view === "all" ? "bg-muted" : "text-muted-foreground"}`}
            >
              All findings
            </button>
          </div>
          <OffencesExportButton jobId={jobId} workspace={workspace} />
          <Button
            variant="outline"
            size="sm"
            onClick={() => setChatOpen((v) => !v)}
            className="gap-1.5"
          >
            <MessageSquare className="h-4 w-4" />
            {chatOpen ? "Hide chat" : "Ask the agent"}
          </Button>
          {!isViewer && (
            <>
              <Button
                variant="outline"
                size="sm"
                disabled={rerunning}
                onClick={async () => {
                  await reRunHeads({ jobId });
                  toast.success("Re-running all offence heads");
                }}
                className="gap-1.5"
              >
                <RefreshCw className="h-4 w-4" />
                Re-run all
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCaseLock({ jobId, locked: !caseLocked })}
                className="gap-1.5"
              >
                {caseLocked ? (
                  <Lock className="h-4 w-4" />
                ) : (
                  <LockOpen className="h-4 w-4" />
                )}
                {caseLocked ? "Case locked" : "Lock case"}
              </Button>
            </>
          )}
        </div>
      </div>

      <div className="mb-4 flex items-center gap-3">
        <span className="flex h-10 w-10 items-center justify-center rounded-full bg-background">
          <Scale className="h-5 w-5 text-muted-foreground" />
        </span>
        <div>
          <h1 className="text-lg font-semibold">Civil & Criminal Offences</h1>
          <p className="text-sm text-muted-foreground">
            {totals.findingCount} finding{totals.findingCount === 1 ? "" : "s"}{" "}
            · {totals.referralCount} referral signal
            {totals.referralCount === 1 ? "" : "s"}
          </p>
        </div>
      </div>

      <div
        className="mb-4 rounded-lg border bg-background p-3 text-sm"
        title={agentText(insolvency.note)}
      >
        <span className="font-medium">Estimated insolvency date: </span>
        {insolvency.date ? fmtDate(insolvency.date) : "unavailable"}
        <span className="text-muted-foreground">
          {" ("}
          {INSOLVENCY_BASIS_LABEL[insolvency.basis] ??
            insolvency.basis.replace(/_/g, " ")}
          {")"}
        </span>
      </div>

      {view === "heads" ? (
        <OffenceMasterTable heads={heads} onOpenHead={setFocusedHead} />
      ) : (
        <OffenceAllFindingsTable
          jobId={jobId}
          heads={heads}
          isViewer={isViewer}
        />
      )}

      <OffenceMasterChatSheet
        open={chatOpen}
        onOpenChange={setChatOpen}
        jobId={jobId}
        agentBusy={workspace.master.running}
        canSend={!isViewer}
      />
    </div>
  );
}
